import React, { useState } from 'react'
import styled from 'styled-components'

import { type UserPayment } from '~/features/mypage/apis/myInfo'
import ChevronDown from '~/shared/assets/icons/ChevronDown'
import ChevronUp from '~/shared/assets/icons/ChevronUp'
import Round from '~/shared/components/round/Round'
import useViewport from '~/shared/hooks/useViewport'
import theme from '~/shared/styles/theme'
import { fDate } from '~/shared/utils/format-date'
import { getColorByPaymentStatusCode } from '~/shared/utils/formatStatus'

interface Props {
  data: UserPayment
  children: React.ReactNode
}

function PurchasedItem({ data, children }: Props) {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const { divide_desc, approved_at, status_desc, status } = data
  const viewport = useViewport()
  const isMobile = viewport < theme.deviceSizes.medium

  const color = getColorByPaymentStatusCode(status)

  return (
    <Wrapper>
      <Title onClick={() => setIsOpen((prev: boolean) => !prev)}>
        <TitleBox>
          <CategoryTag>{divide_desc}</CategoryTag>
          {!isMobile && (
            <DateAndStatusBox>
              <StatusBox>
                <Round color={color} />
                <StatusText $color={color}>{status_desc}</StatusText>
              </StatusBox>
              <TitleText>
                {fDate(approved_at, 'yyyy년 MM월 dd일 HH:mm:ss')}
              </TitleText>
            </DateAndStatusBox>
          )}
        </TitleBox>

        {isOpen ? <ChevronUp /> : <ChevronDown />}
      </Title>
      {isOpen && typeof children === 'object' ? (
        <Contents>{children}</Contents>
      ) : null}
    </Wrapper>
  )
}

export default PurchasedItem

const Wrapper = styled.li`
  cursor: pointer;

  /* position: relative; */

  display: flex;
  flex-direction: column;
  justify-content: center;

  box-sizing: border-box;
  width: 100%;
  height: auto;
  padding: 0 24px;

  list-style: none;

  border: 1px solid #c1c3c2;
  border-radius: 12px;

  @media ${theme.device.medium} {
    flex-direction: column;
    align-items: flex-start;
    padding: 16px;
  }
`
const Title = styled.div`
  cursor: pointer;

  position: relative;

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  height: 85px;

  font-family: NanumSquareNeo-Regular, sans-serif;

  .faq-icon {
    margin-right: 16px;
  }

  .chevron-icon {
    position: absolute;
    right: 0;
    color: #94a3b8;
  }

  @media ${theme.device.medium} {
    align-items: center;
    width: 100%;
    height: 100%;
  }
`

const TitleBox = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;
  justify-content: space-between;

  box-sizing: border-box;
  width: 100%;

  @media ${theme.device.medium} {
    flex-direction: column;
    gap: 12px;
    align-items: start;
  }
`

const StatusBox = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: center;

  @media ${theme.device.medium} {
    gap: 8px;
  }
`

const StatusText = styled.span<{ $color: string }>`
  width: 100%;

  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 25px;
  color: ${({ $color }) => $color};

  @media ${theme.device.medium} {
    font-size: 15px;
    line-height: 21px;
  }
`

const DateAndStatusBox = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: end;

  width: 100%;

  text-align: right;

  @media ${theme.device.medium} {
    flex-direction: column;

    /* align-items: center; */
    align-items: flex-start;
    width: 100%;
    padding-left: 20px;

    /* text-align: left; */
  }
`

const CategoryTag = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;
  width: 164px;
  height: 41px;
  padding: 16px 8px;

  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 25px;
  color: #313634;

  background-color: #eaebeb;
  border-radius: 60px;

  @media ${theme.device.medium} {
    width: 134px;
    height: 37px;
    padding: 12px 8px;

    font-size: 15px;
    line-height: 21px;
  }
`

const TitleText = styled.div`
  overflow: hidden;

  font-size: 18px;
  font-weight: 500;
  line-height: 33px;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media ${theme.device.medium} {
    word-wrap: break-word;
    white-space: pre-wrap;
  }
`

const Contents = styled.p`
  position: sticky;
  top: 50px;

  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: flex-start;
  align-self: stretch;

  box-sizing: border-box;
  width: 100%;
  margin-bottom: 20px;
  padding: 24px 36px;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 16px;
  font-weight: 400;
  font-style: normal;
  line-height: 28px;

  border-radius: 4px 24px 24px;

  @media ${theme.device.medium} {
    padding: 24px 20px;
  }
`
